import ReactDOM from 'react-dom/client'
import { registerSW } from 'virtual:pwa-register'
import App from './App.jsx'
import './index.css'

if (import.meta.env.DEV) {
  // Local development must never be controlled by yesterday's production
  // preview worker. Otherwise visible UI changes can appear "not applied".
  navigator.serviceWorker?.getRegistrations()
    .then((registrations) => Promise.all(registrations.map((registration) => registration.unregister())))
    .catch(() => {});
  if ('caches' in window) {
    window.caches.keys()
      .then((keys) => Promise.all(keys.map((key) => window.caches.delete(key))))
      .catch(() => {});
  }
} else {
  // Register service worker with auto-update.
  //
  // registerType: 'autoUpdate' swaps the new worker in silently (skipWaiting +
  // clientsClaim) but it does NOT reload the page on its own in this setup — so on
  // its own the new version stays invisible until a manual hard-refresh. The
  // controllerchange listener below is the actual auto-update: it reloads the tab
  // once, the moment a new worker takes control. This ran Feb–Apr 2026 with no
  // issues; it was wrongly deleted as a "duplicate" on 2026-04-12 (commit 01e80c82),
  // which is what broke auto-update, and recovered on 2026-06-22.
  //
  // We check for a new version when the user RETURNS to the tab — not on a timer.
  // A 60-second poll forced reloads mid-session ("blank when I navigate back");
  // checking on visibility means the app only refreshes at a natural moment, and
  // only if a deploy actually happened.
  registerSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      if (!registration) return;
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          registration.update();
        }
      });
    }
  })

  // THE auto-update. When a new service worker takes control (a deploy landed
  // while the app was open), reload once to pick up the fresh assets — no banner,
  // no toast, no prompt. Two guards make a loop impossible:
  //   - swControlling: skips the very first activation (initial install), so a
  //     fresh visit doesn't reload itself.
  //   - reloading: ensures the reload fires exactly once even if the event repeats.
  let swControlling = !!navigator.serviceWorker?.controller;
  let reloading = false;
  navigator.serviceWorker?.addEventListener('controllerchange', () => {
    if (!swControlling) { swControlling = true; return; }
    if (reloading) return;
    reloading = true;
    window.location.reload();
  })
}

// Import seed utilities for console access (admin operations)
import './utils/seedConsequenceTypes.js'
import './utils/seedTimeframes.js'
import './utils/seedStudentHistoryFilters.js'
import './utils/seedGrades.js'

// ⚠️ WARNING: DO NOT ADD React.StrictMode HERE
// React.StrictMode causes @dnd-kit libraries to fail completely in development.
// The double-mounting behavior of StrictMode breaks drag-and-drop functionality
// across the entire application (menus, values, consequences, seating).
// This is a known incompatibility between @dnd-kit v6 and React 18.
// See DRAG_DROP_ISSUE_REPORT.md for full details.
//
// If you need to re-enable StrictMode, you must either:
// 1. Wait for @dnd-kit to fix this issue (check their GitHub)
// 2. Replace all @dnd-kit usage with an alternative drag-and-drop library
// 3. Accept that drag-and-drop won't work in development (works in production)
ReactDOM.createRoot(document.getElementById('root')).render(
  <App />
)
